import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Label } from '@/shared/components/ui/label';
import { Input } from '@/shared/components/ui/input';
import { Switch } from '@/shared/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { FieldDescription } from '@/shared/components/ui/field';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { PillRadioGroup } from '@/shared/components/custom/pill-radio-group';
import { useTimeOffPolicy } from '../../hooks/useTimeOffPolicy';
import { useUpdateTimeOffPolicy } from '../../hooks/useUpdateTimeOffPolicy';
import {
  policySchema,
  APPROVAL_LEVEL_OPTIONS,
  RESPONSE_LIMIT_UNIT_OPTIONS,
  ON_EXHAUSTED_OPTIONS,
  CARRY_OVER_MODE_OPTIONS,
  type PolicyFormInput,
  type PolicyFormValues,
} from '../../schemas';

const DEFAULT_VALUES: PolicyFormValues = {
  approvalLevel: APPROVAL_LEVEL_OPTIONS[0],
  responseLimit: 0,
  responseLimitUnit: RESPONSE_LIMIT_UNIT_OPTIONS[0],
  onExhausted: ON_EXHAUSTED_OPTIONS[0],
  minAdvanceBusinessDays: 0,
  resolutionMarginBusinessDays: 0,
  graceToleranceDays: 0,
  carryOverMode: CARRY_OVER_MODE_OPTIONS[0],
  allowAdvance: false,
  maxAdvanceDays: 0,
};

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="space-y-1">
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function PolicyRow({
  label,
  disabled,
  children,
}: {
  label: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-4 py-4',
        disabled && 'opacity-50',
      )}
    >
      <Label className={disabled ? 'pointer-events-none' : undefined}>{label}</Label>
      {children}
    </div>
  );
}

export function PolicyForm() {
  const { t } = useTranslation('timeOff');
  const { data, isLoading, isError } = useTimeOffPolicy();
  const updatePolicy = useUpdateTimeOffPolicy();

  const approvalLevelOptions = APPROVAL_LEVEL_OPTIONS.map((value) => ({
    value,
    label: t(`settings.policy.options.approvalLevel.${value}`),
  }));
  const responseLimitUnitOptions = RESPONSE_LIMIT_UNIT_OPTIONS.map((value) => ({
    value,
    label: t(`settings.policy.options.responseLimitUnit.${value}`),
  }));
  const onExhaustedOptions = ON_EXHAUSTED_OPTIONS.map((value) => ({
    value,
    label: t(`settings.policy.options.onExhausted.${value}`),
  }));
  const carryOverModeOptions = CARRY_OVER_MODE_OPTIONS.map((value) => ({
    value,
    label: t(`settings.policy.options.carryOverMode.${value}`),
  }));

  const { control, handleSubmit, reset, watch } = useForm<
    PolicyFormInput,
    unknown,
    PolicyFormValues
  >({
    resolver: zodResolver(policySchema(t)),
    defaultValues: DEFAULT_VALUES,
  });

  const allowAdvance = watch('allowAdvance');

  function buildFormValues(): PolicyFormValues {
    if (!data) return DEFAULT_VALUES;
    return {
      ...data,
      approvalLevel: (data.approvalLevel ??
        APPROVAL_LEVEL_OPTIONS[0]) as PolicyFormValues['approvalLevel'],
      responseLimitUnit: (data.responseLimitUnit ??
        RESPONSE_LIMIT_UNIT_OPTIONS[0]) as PolicyFormValues['responseLimitUnit'],
      onExhausted: (data.onExhausted ?? ON_EXHAUSTED_OPTIONS[0]) as PolicyFormValues['onExhausted'],
      carryOverMode: (data.carryOverMode ??
        CARRY_OVER_MODE_OPTIONS[0]) as PolicyFormValues['carryOverMode'],
    };
  }

  useEffect(() => {
    if (data) reset(buildFormValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, reset]);

  function handleCancel() {
    reset(buildFormValues());
  }

  function onSubmit(values: PolicyFormValues) {
    updatePolicy.mutate(values);
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="time-off-settings-policy-loading-skeleton">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="time-off-settings-policy-error">
        {t('apiErrors.policy.default')}
      </p>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-8"
      data-testid="time-off-settings-policy-form"
    >
      <section>
        <SectionHeader
          title={t('settings.policy.sections.approval.title')}
          description={t('settings.policy.sections.approval.description')}
        />
        <div className="mt-4 divide-y divide-border">
          <PolicyRow label={t('settings.policy.approvalLevel')}>
            <Controller
              name="approvalLevel"
              control={control}
              render={({ field }) => (
                <PillRadioGroup
                  id="approvalLevel"
                  value={field.value}
                  onValueChange={field.onChange}
                  options={approvalLevelOptions}
                  data-testid="time-off-settings-policy-approval-level"
                />
              )}
            />
          </PolicyRow>
        </div>
      </section>

      <section>
        <SectionHeader
          title={t('settings.policy.sections.timeout.title')}
          description={t('settings.policy.sections.timeout.description')}
        />
        <div className="mt-4 divide-y divide-border border-t">
          <PolicyRow label={t('settings.policy.sections.timeout.autoResolve')}>
            <Switch checked disabled data-testid="time-off-settings-policy-auto-resolve" />
          </PolicyRow>
          <PolicyRow label={t('settings.policy.responseLimit')}>
            <div className="flex items-center gap-2">
              <Controller
                name="responseLimit"
                control={control}
                render={({ field }) => (
                  <Input
                    type="number"
                    value={field.value as number}
                    onChange={field.onChange}
                    className="w-20"
                    data-testid="time-off-settings-policy-response-limit"
                  />
                )}
              />
              <Controller
                name="responseLimitUnit"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger data-testid="time-off-settings-policy-response-limit-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {responseLimitUnitOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </PolicyRow>
          <PolicyRow label={t('settings.policy.onExhausted')}>
            <Controller
              name="onExhausted"
              control={control}
              render={({ field }) => (
                <PillRadioGroup
                  id="onExhausted"
                  value={field.value}
                  onValueChange={field.onChange}
                  options={onExhaustedOptions}
                  data-testid="time-off-settings-policy-on-exhausted"
                />
              )}
            />
          </PolicyRow>
        </div>
      </section>

      <section>
        <SectionHeader
          title={t('settings.policy.sections.advanceNotice.title')}
          description={t('settings.policy.sections.advanceNotice.description')}
        />
        <div className="mt-4 grid grid-cols-1 gap-6 border-t pt-4 sm:grid-cols-3">
          <div>
            <FormFieldInput
              control={control}
              name="minAdvanceBusinessDays"
              type="number"
              label={t('settings.policy.minAdvanceBusinessDays')}
              data-testid="time-off-settings-policy-min-advance"
            />
            <FieldDescription>{t('settings.policy.minAdvanceHelp')}</FieldDescription>
          </div>
          <div>
            <FormFieldInput
              control={control}
              name="resolutionMarginBusinessDays"
              type="number"
              label={t('settings.policy.resolutionMarginBusinessDays')}
              data-testid="time-off-settings-policy-resolution-margin"
            />
            <FieldDescription>{t('settings.policy.resolutionMarginHelp')}</FieldDescription>
          </div>
          <div>
            <FormFieldInput
              control={control}
              name="graceToleranceDays"
              type="number"
              label={t('settings.policy.graceToleranceDays')}
              data-testid="time-off-settings-policy-grace-tolerance"
            />
            <FieldDescription>{t('settings.policy.graceToleranceHelp')}</FieldDescription>
          </div>
        </div>
      </section>

      <section>
        <SectionHeader
          title={t('settings.policy.sections.carryOver.title')}
          description={t('settings.policy.sections.carryOver.description')}
        />
        <div className="mt-4 divide-y divide-border border-t">
          <PolicyRow label={t('settings.policy.carryOverMode')}>
            <Controller
              name="carryOverMode"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger data-testid="time-off-settings-policy-carry-over-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {carryOverModeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </PolicyRow>
          <PolicyRow label={t('settings.policy.allowAdvance')}>
            <Controller
              name="allowAdvance"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={!!field.value}
                  onCheckedChange={field.onChange}
                  data-testid="time-off-settings-policy-allow-advance"
                />
              )}
            />
          </PolicyRow>
          <PolicyRow label={t('settings.policy.maxAdvanceDays')} disabled={!allowAdvance}>
            <Controller
              name="maxAdvanceDays"
              control={control}
              render={({ field }) => (
                <Input
                  type="number"
                  value={field.value as number}
                  onChange={field.onChange}
                  disabled={!allowAdvance}
                  className="w-20"
                  data-testid="time-off-settings-policy-max-advance-days"
                />
              )}
            />
          </PolicyRow>
          <FieldDescription>{t('settings.policy.maxAdvanceHelp')}</FieldDescription>
        </div>
      </section>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={handleCancel}>
          {t('settings.policy.cancel')}
        </Button>
        <Button type="submit" disabled={updatePolicy.isPending}>
          {t('settings.policy.save')}
        </Button>
      </div>
    </form>
  );
}
